import { FC, useMemo } from "react";
import { Formik, FormikHelpers } from "formik";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Alert from "react-bootstrap/Alert";
import {
    ReportAccessInformationFileFieldsFragment,
    useReportAccessInformationFileQuery,
    useUpdateReportAccessInformationFileMutation,
} from "app/api/graph/types";
import yup from "app/util/yup.ts";
import { StatusAlert } from "app/component/form/StatusAlert.tsx";
import { RichTextEditor } from "app/component/form/text-editor/RichTextEditor.tsx";
import { handleError } from "app/api/graph/form.ts";
import { reportAccessInformationFileGraphId } from "app/api/graph/helpers.ts";
import { LoadingSpinner } from "app/component/util/LoadingSpinner.tsx";

type Props = {
    fileId: number;
    onUpdate?: (reportAccessInformationFile: ReportAccessInformationFileFieldsFragment) => void;
};

type FormValues = {
    description?: string;
};

const schema: yup.ObjectSchema<FormValues> = yup.object({
    description: yup.string(),
});

export const UpdateReportAccessInformationFileForm: FC<Props> = ({ fileId, onUpdate }) => {
    const { data, loading, error } = useReportAccessInformationFileQuery({
        variables: { fileId: reportAccessInformationFileGraphId(fileId) },
    });
    const reportAccessInformationFile = data?.reportAccessInformationFile;
    const [updateAccessInformationFile] = useUpdateReportAccessInformationFileMutation({});

    const initialValues: FormValues = useMemo(
        () => ({
            description: reportAccessInformationFile?.description ?? undefined,
        }),
        [reportAccessInformationFile],
    );

    const onSubmit = (values: FormValues, actions: FormikHelpers<FormValues>) =>
        updateAccessInformationFile({
            variables: {
                input: {
                    id: reportAccessInformationFileGraphId(fileId),
                    description: values.description,
                },
            },
        })
            .then((response) =>
                response.data?.updateReportAccessInformationFile?.reportAccessInformationFile
                    ? onUpdate?.(response.data?.updateReportAccessInformationFile?.reportAccessInformationFile)
                    : undefined,
            )
            .catch((error) => handleError(error, actions))
            .finally(() => actions.setSubmitting(false));

    if (loading) {
        return <LoadingSpinner />;
    }
    if (error) {
        return <Alert variant="danger">{error.message}</Alert>;
    }
    if (!reportAccessInformationFile) {
        return <Alert variant="danger">Not found.</Alert>;
    }

    return (
        <Formik validationSchema={schema} onSubmit={onSubmit} initialValues={initialValues}>
            {({ handleSubmit, setFieldValue, setFieldTouched, values, isSubmitting, errors, touched, status }) => (
                <Form noValidate onSubmit={handleSubmit}>
                    <StatusAlert status={status as unknown} />

                    <Form.Group className="mb-3">
                        <Form.Label>Description</Form.Label>
                        <RichTextEditor
                            value={values.description ?? ""}
                            onChange={(value) => setFieldValue("description", value)}
                            onBlur={() => setFieldTouched("description")}
                            isInvalid={touched.description && !!errors.description}
                        />
                        <Form.Control.Feedback type="invalid">{errors.description}</Form.Control.Feedback>
                    </Form.Group>

                    <Button type="submit" disabled={isSubmitting}>
                        Submit
                    </Button>
                </Form>
            )}
        </Formik>
    );
};
